import { useState, useEffect } from 'react';
import { Deployment, Config, HealthcheckType, AppHealthcheckMapping } from '../types';

interface HealthCheckSettingsProps {
  deployment: Deployment;
  config: Config | null;
  onSave: (mapping: AppHealthcheckMapping) => Promise<void>;
}

interface KeyValuePair {
  key: string;
  value: string;
}

export function HealthCheckSettings({ deployment, config, onSave }: HealthCheckSettingsProps) {
  const [healthcheckType, setHealthcheckType] = useState<HealthcheckType>('None');
  const [urlValue, setUrlValue] = useState('');
  const [keyValuePairs, setKeyValuePairs] = useState<KeyValuePair[]>([{ key: '', value: '' }]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Find existing mapping for this Env+App
  useEffect(() => {
    if (!config?.appHealthcheckMappings) return;

    const existingMapping = config.appHealthcheckMappings.find(
      (m) => m.Env === deployment.Env && m.App === deployment.App
    );

    if (existingMapping) {
      setHealthcheckType(existingMapping.healthcheck_type || 'None');

      if (existingMapping.healthcheck_type === 'URL') {
        setUrlValue(existingMapping.data?.URL || '');
      } else if (existingMapping.healthcheck_type === 'SSMParam' || existingMapping.healthcheck_type === 'TextFile') {
        const pairs = Object.entries(existingMapping.data || {}).map(([key, value]) => ({
          key,
          value: value as string,
        }));
        setKeyValuePairs(pairs.length > 0 ? pairs : [{ key: '', value: '' }]);
      }
    }
  }, [config, deployment.Env, deployment.App]);

  const handleTypeChange = (newType: HealthcheckType) => {
    setHealthcheckType(newType);
    setSaveSuccess(false);
    setSaveError(null);

    // Reset data fields when type changes
    if (newType === 'URL') {
      setUrlValue('');
      setKeyValuePairs([{ key: '', value: '' }]);
    } else if (newType === 'SSMParam' || newType === 'TextFile') {
      setUrlValue('');
      setKeyValuePairs([{ key: '', value: '' }]);
    } else {
      setUrlValue('');
      setKeyValuePairs([{ key: '', value: '' }]);
    }
  };

  const handleAddKeyValue = () => {
    setKeyValuePairs([...keyValuePairs, { key: '', value: '' }]);
  };

  const handleRemoveKeyValue = (index: number) => {
    if (keyValuePairs.length > 1) {
      setKeyValuePairs(keyValuePairs.filter((_, i) => i !== index));
    }
  };

  const handleKeyValueChange = (index: number, field: 'key' | 'value', newValue: string) => {
    const updated = [...keyValuePairs];
    updated[index][field] = newValue;
    setKeyValuePairs(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Build the data object based on type
      let data: Record<string, string> = {};

      if (healthcheckType === 'URL') {
        data = { URL: urlValue };
      } else if (healthcheckType === 'SSMParam' || healthcheckType === 'TextFile') {
        keyValuePairs.forEach((pair) => {
          if (pair.key.trim()) {
            data[pair.key.trim()] = pair.value;
          }
        });
      }

      const mapping: AppHealthcheckMapping = {
        Account: deployment.Account || '',
        Env: deployment.Env,
        App: deployment.App,
        healthcheck_type: healthcheckType,
        data,
      };

      await onSave(mapping);
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const healthcheckTypes = config?.healthcheckTypes || ['URL', 'SSMParam', 'TextFile', 'None'];

  return (
    <div className="healthcheck-settings">
      {/* Read-only deployment info */}
      <div className="healthcheck-readonly">
        <div className="detail-row">
          <span className="label">Account:</span>
          <span className="value">{deployment.Account || '-'}</span>
        </div>
        <div className="detail-row">
          <span className="label">Environment:</span>
          <span className="value">{deployment.Env}</span>
        </div>
        <div className="detail-row">
          <span className="label">Application:</span>
          <span className="value">{deployment.App}</span>
        </div>
      </div>

      {/* Health Check Type dropdown */}
      <div className="healthcheck-form-group">
        <label htmlFor="healthcheck-type">Health Check Type</label>
        <select
          id="healthcheck-type"
          value={healthcheckType}
          onChange={(e) => handleTypeChange(e.target.value as HealthcheckType)}
        >
          {healthcheckTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      {/* Dynamic data fields based on type */}
      {healthcheckType === 'URL' && (
        <div className="healthcheck-form-group">
          <label htmlFor="healthcheck-url">Health Check URL</label>
          <input
            id="healthcheck-url"
            type="text"
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            placeholder="https://example.com/health"
          />
        </div>
      )}

      {(healthcheckType === 'SSMParam' || healthcheckType === 'TextFile') && (
        <div className="healthcheck-kvp-container">
          <label>Parameters</label>
          {keyValuePairs.map((pair, index) => (
            <div key={index} className="healthcheck-kvp-row">
              <input
                type="text"
                value={pair.key}
                onChange={(e) => handleKeyValueChange(index, 'key', e.target.value)}
                placeholder="Parameter name"
              />
              <input
                type="text"
                value={pair.value}
                onChange={(e) => handleKeyValueChange(index, 'value', e.target.value)}
                placeholder="Value"
              />
              <button
                type="button"
                className="healthcheck-remove-btn"
                onClick={() => handleRemoveKeyValue(index)}
                disabled={keyValuePairs.length === 1}
              >
                ×
              </button>
            </div>
          ))}
          <button type="button" className="healthcheck-add-btn" onClick={handleAddKeyValue}>
            + Add Parameter
          </button>
        </div>
      )}

      {healthcheckType === 'None' && (
        <p className="healthcheck-none-message">No health check configuration needed.</p>
      )}

      {/* Save button and status */}
      <div className="healthcheck-actions">
        <button
          className="healthcheck-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        {saveError && <p className="healthcheck-save-error">{saveError}</p>}
        {saveSuccess && <p className="healthcheck-save-success">Saved successfully!</p>}
      </div>
    </div>
  );
}
